import {HTTP} from "@/http";

class AdminAccountService {
  basePath = 'api/v2/admin/accounts';

  /**
   * Get all accounts (for Admins)
   * @param {Object} filterParams - Filter parameters
   * @param {string} filterParams.search - Optional search term
   * @param {boolean} filterParams.localOnly - Optional filter for local accounts only (true=local, false=LDAP, null=both)
   * @param {Object} pageParams - Pagination parameters
   * @param {number} pageParams.page - Page number (0-based)
   * @param {number} pageParams.pageSize - Page size
   * @param {string} pageParams.pageSortDirection - Sort direction ('ASC' or 'DESC')
   * @returns {Promise<Object>} Promise that resolves to the accounts response
   */
  getAllAccounts(filterParams = {}, pageParams = {}) {
    const params = {
      ...filterParams,
      ...pageParams
    };

    return HTTP.get(this.basePath, { params });
  }

  getAllAccountsCount() {
    return HTTP.get(this.basePath + "/count");
  }

  /**
   * Get an account by ID
   * @param {string} accountId - The account ID
   * @returns {Promise<Object>} Promise that resolves to the account response
   */
  getAccountById(accountId) {
    return HTTP.get(`${this.basePath}/${accountId}`);
  }

  /**
   * Create a new account
   * @param {Object} account - The account to create with customerId
   * @returns {Promise<Object>} Promise that resolves to the created account
   */
  createAccount(account) {
    // Wir übergeben nur das account-Objekt, das bereits customerId enthält
    // tenantId ist nicht notwendig, da es vom Backend aus der customerId ermittelt wird
    return HTTP.post(this.basePath, account);
  }

  /**
   * Update an account
   * @param {string} accountId - The account ID to update
   * @param {Object} account - The account data to update
   * @returns {Promise<Object>} Promise that resolves to the updated account
   */
  updateAccount(accountId, account) {
    console.log(account);
    return HTTP.patch(`${this.basePath}/${accountId}`, account);
  }

  /**
   * Delete an account
   * @param {string} accountId - The account ID to delete
   * @returns {Promise<Object>} Promise that resolves when the account is deleted
   */
  deleteAccount(accountId) {
    return HTTP.delete(`${this.basePath}/${accountId}`);
  }

  /**
   * Set an account active state
   * @param {string} accountId - The account ID
   * @param {boolean} isActive - Whether the account should be active
   * @returns {Promise<Object>} Promise that resolves when the state is set
   */
  setAccountActive(accountId, isActive) {
    return HTTP.post(`${this.basePath}/${accountId}/active`, { active: isActive });
  }

  /**
   * Send a test email to an account
   * @param {string} accountId - The account ID
   * @returns {Promise<Object>} Promise that resolves when the email is sent
   */
  sendTestEmail(accountId) {
    return HTTP.post(`${this.basePath}/${accountId}/test/mail`);
  }

  /**
   * Send a test push notification to an account
   * @param {string} accountId - The account ID
   * @returns {Promise<Object>} Promise that resolves when the push notification is sent
   */
  sendTestPush(accountId) {
    return HTTP.post(`${this.basePath}/${accountId}/test/push`);
  }

  /**
   * Delete all device tokens for an account
   * @param {string} accountId - The account ID
   * @returns {Promise<Object>} Promise that resolves to a message response
   */
  deleteAllDeviceTokens(accountId) {
    return HTTP.delete(`${this.basePath}/${accountId}/deviceToken/all`);
  }

  /**
   * Delete all device tokens for all accounts in a tenant
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Promise that resolves to a message response
   */
  deleteAllDeviceTokensByTenant(tenantId) {
    return HTTP.delete(`${this.basePath}/deviceToken/all`, { params: { tenantId } });
  }
}

/**
 * @type {AdminAccountService}
 */
export const adminAccountService = new AdminAccountService();
